import { Form } from 'factorial-form'
import { autorun } from 'mobx'

import asyncAction from 'modules/core/lib/asyncAction'

import { Recipients } from 'modules/automations/components/Show/CommonNotification/utils'
import type { Data } from 'modules/automations/components/Show/CommonNotification/utils'
import {
  accessGroupToRecipient,
  accessGroupToRecipientType,
  buildAccessGroupPayload,
} from 'modules/automations/components/Show/CommonNotification/utils'
import { buildCustomTemplatePayload } from 'modules/automations/components/Show/SendCustomNotification/utils'
import { accessGroups } from 'modules/automations/store/collections/accessGroups'
import type { Action } from 'modules/automations/store/models/Action'
import { templates } from 'modules/custom_resources/store/collections/customTemplates/templates'
import type { Template } from 'modules/custom_resources/store/models/customTemplates/Template'

import type { Storeable } from './Storeable'

export class SendCustomNotification implements Storeable {
  #action: Action
  disposer: () => void
  form: Form

  constructor(action: Action) {
    this.#action = action
    this.form = new Form(
      {},
      {
        automations_action_id: 'number',
        recipient_type: 'string',
        recipient: 'string',
        notifiable_id: 'number',
        notifiable_ids: 'any', // Form doesn't support array types
        subject_template_body: 'string',
        body_template_body: 'string',
      }
    )
    this.disposer = autorun(() => this._updateForm())
  }

  dispose() {
    this.disposer()
  }

  _updateForm() {
    const accessGroup = this.#action.accessGroup
    const subjectTemplate = this.#action.subjectTemplate
    const bodyTemplate = this.#action.bodyTemplate

    if (!accessGroup) {
      return this.form.setValues({
        automations_action_id: this.#action.attributes.id,
        subject_template_body: subjectTemplate?.attributes.body,
        body_template_body: bodyTemplate?.attributes.body,
      })
    }
    const recipientType = accessGroupToRecipientType(accessGroup)
    const recipient = accessGroupToRecipient(accessGroup)
    const notifiableIds =
      recipient === Recipients.access &&
      !accessGroup.attributes.notifiable_ids?.length &&
      accessGroup.attributes.notifiable_id
        ? [accessGroup.attributes.notifiable_id]
        : accessGroup.attributes.notifiable_ids

    return this.form.setValues({
      automations_action_id: this.#action.attributes.id,
      recipient_type: recipientType,
      recipient: recipient,
      notifiable_id: accessGroup.attributes.notifiable_id,
      notifiable_ids: notifiableIds,
      subject_template_body: subjectTemplate?.attributes.body,
      body_template_body: bodyTemplate?.attributes.body,
    })
  }

  async _saveAccessGroup() {
    const accessGroup = this.#action.accessGroup
    // TODO: we should be able to type `Form` to avoid this dangerous
    //       type conversion.
    const payload = buildAccessGroupPayload(this.form.data() as Data)

    return accessGroup
      ? accessGroup.update(payload).then(() => accessGroup)
      : accessGroups.create(payload)
  }

  async _saveTemplate(customTemplate: Template | null | undefined, data: string) {
    const payload = buildCustomTemplatePayload(data)

    return customTemplate
      ? customTemplate.update(payload).then(() => customTemplate)
      : templates.create(payload)
  }

  async save() {
    const subjectTemplate = this.#action.subjectTemplate
    const bodyTemplate = this.#action.bodyTemplate

    const subjectFormValue = this.form.get('subject_template_body').value
    const bodyFormValue = this.form.get('body_template_body').value

    let subjectTemplateResult
    let bodyTemplateResult
    let recipientsResult

    if (this.form.get('recipient').value) {
      recipientsResult = await asyncAction(() => {
        return this._saveAccessGroup()
      })
    }

    if (subjectFormValue != '') {
      subjectTemplateResult = await asyncAction(() => {
        return this._saveTemplate(subjectTemplate, subjectFormValue)
      })
    }

    if (bodyFormValue != '') {
      bodyTemplateResult = await asyncAction(() => {
        return this._saveTemplate(bodyTemplate, bodyFormValue)
      })
    }

    const successful =
      (subjectTemplateResult?.success ?? true) &&
      (bodyTemplateResult?.success ?? true) &&
      (recipientsResult?.success ?? true)

    if (successful && (!bodyTemplate || !subjectTemplate)) {
      const subject = subjectTemplateResult?.value
      const body = bodyTemplateResult?.value

      const subjectId = typeof subject === 'object' && 'id' in subject && subject.id
      const bodyId = typeof body === 'object' && 'id' in body && body.id

      await asyncAction(() =>
        this.#action.save({
          metadata: {
            ...(bodyId && { body_id: bodyId }),
            ...(subjectId && { subject_id: subjectId }),
          },
        })
      )
    }
  }
}
